// ✅ src/components/ElevatorPitchRecorder.js
import React, { useState, useRef } from "react";
import { FaMicrophone, FaStop, FaPlay, FaTrashAlt, FaRobot } from "react-icons/fa";

const ElevatorPitchRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [aiPitch, setAiPitch] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const handleRecordClick = async () => {
    try {
      if (!isRecording) {
        setIsRecording(true);
        setTranscript("");
        setAudioUrl(null);
        setAiPitch("");
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          chunksRef.current.push(event.data);
        };

        mediaRecorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);

          // Upload the audio file for transcription
          try {
            const formData = new FormData();
            formData.append("file", blob);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/elevator-pitch/upload`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            });

            const result = await response.json();
            setTranscript(result.transcript || "No transcript available.");
          } catch (err) {
            console.error("❌ Failed to process pitch:", err);
            setTranscript("Error processing your pitch. Please try again.");
          }
        };

        mediaRecorder.start();
      } else {
        setIsRecording(false);
        mediaRecorderRef.current.stop();
      }
    } catch (err) {
      console.error("❌ Error accessing microphone:", err);
      setTranscript("Error accessing your microphone. Please try again.");
    }
  };

  const handlePlayClick = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setAudioUrl(null);
    setTranscript("");
    setAiPitch("");
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleGenerateAiPitch = async () => {
    try {
      if (!transcript) {
        setAiPitch("Please record a pitch first.");
        return;
      }

      setIsProcessing(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/elevator-pitch/ai`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ transcript }),
      });

      const result = await response.json();
      setAiPitch(result.pitch || "No AI pitch available.");
    } catch (err) {
      console.error("❌ Failed to generate AI pitch:", err);
      setAiPitch("Error generating AI pitch. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-50 flex flex-col items-center gap-4">
      <button
        onClick={handleRecordClick}
        disabled={isProcessing}
        className={`w-20 h-20 flex items-center justify-center rounded-full transition-all duration-300 ${
          isRecording
            ? "bg-red-600 shadow-[0_0_30px_rgba(255,82,82,0.7)] animate-ping"
            : "bg-red-500 hover:bg-red-600 shadow-lg"
        }`}
      >
        {isRecording ? (
          <FaStop className="text-white text-3xl" />
        ) : (
          <FaMicrophone className="text-white text-3xl" />
        )}
      </button>

      {audioUrl && !isRecording && (
        <div className="flex gap-4">
          <button
            onClick={handlePlayClick}
            className="w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center"
          >
            <FaPlay className="text-2xl" />
          </button>

          <button
            onClick={handleDeleteClick}
            className="w-14 h-14 bg-gray-500 hover:bg-gray-600 text-white rounded-full shadow-lg flex items-center justify-center"
          >
            <FaTrashAlt className="text-2xl" />
          </button>

          <button
            onClick={handleGenerateAiPitch}
            disabled={isProcessing || !transcript}
            className="w-14 h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center"
          >
            <FaRobot className="text-2xl" />
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="bg-white rounded-lg p-4 shadow-lg flex flex-col items-center mt-4">
          <p className="text-gray-800 font-semibold mb-2">
            Are you sure you want to delete this recording?
          </p>
          <div className="flex gap-4">
            <button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white py-1 px-4 rounded-lg"
            >
              Yes, Delete
            </button>
            <button
              onClick={cancelDelete}
              className="bg-gray-500 hover:bg-gray-600 text-white py-1 px-4 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {transcript && (
        <p className="mt-3 bg-gray-200 text-gray-800 p-3 rounded-lg max-w-xs break-words shadow-md">
          {transcript}
        </p>
      )}
      {aiPitch && (
        <p className="mt-3 bg-blue-200 text-blue-800 p-3 rounded-lg max-w-xs break-words shadow-md">
          {aiPitch}
        </p>
      )}
    </div>
  );
};

export default ElevatorPitchRecorder;
